import { Routes } from '@angular/router';

import { AppLayoutComponent } from '@app/layout/components/app.layout';
import { staleVersionGuard } from '@core/guards/stale-version.guard';
import {
  PERMISSION_MODULES,
  PERMISSIONS_GESTION_DE_RESELLERS,
  PERMISSIONS_SOPORTE,
  PERMISSIONS_VENTAS,
} from '@auth/constants';
import {
  cashierOnlyRedirectGuard,
  hasModuleAccessGuard,
  hasAnyModuleAccessGuard,
  hasPermissionGuard,
  isAuthenticatedGuard,
  mustChangePasswordGuard,
  isDefaultTenantGuard,
  isResellerUserGuard,
  notAuthenticatedGuard,
  posAccessGuard,
} from '@auth/guards';

export const appRoutes: Routes = [
  {
    path: 'delivery/dispatch-guides/report/verify',
    loadComponent: () =>
      import('@features/sales/dispatch-guides/pages/public-dispatch-guide-report-verify/public-dispatch-guide-report-verify.page'),
  },
  {
    path: 'delivery/dispatch-guides/:token',
    loadComponent: () =>
      import('@features/sales/dispatch-guides/pages/public-dispatch-guide-delivery/public-dispatch-guide-delivery.page'),
  },
  // Planificador de rutas de despacho: vista full-screen SIN barra lateral del menú
  // (fuera de AppLayout, como POS). Debe declararse antes de la ruta del layout para
  // que matchee primero. La LISTA de rutas sí vive dentro del layout (en sales.routes).
  //
  // Fix F5 (recarga en frío): el guard de auth (async, espera la rehidratación de sesión) va en el PADRE
  // componentless y el de permiso en los HIJOS. Así los guards corren en SECUENCIA padre→hijo (no en
  // paralelo dentro de un mismo `canActivate`), y en un F5 el `hasPermissionGuard` ya lee la sesión
  // rehidratada. Antes, con ambos guards en el mismo array, el de permiso (síncrono) corría con el auth
  // aún vacío y redirigía al login → dashboard. El padre sin componente renderiza el hijo en el outlet
  // raíz, conservando la vista full-screen.
  //
  // `mustChangePasswordGuard` acompaña al de auth en el PADRE por el mismo motivo que en `/pos`:
  // esta vista cuelga de la raíz y no hereda el guard del AppLayout.
  {
    path: 'sales/dispatch-routes/plan',
    canActivate: [isAuthenticatedGuard, mustChangePasswordGuard],
    children: [
      {
        path: '',
        canActivate: [hasPermissionGuard(PERMISSIONS_VENTAS.VER_RUTAS_DE_DESPACHO)],
        loadComponent: () =>
          import('@features/sales/dispatch-routes/pages/dispatch-route-planner/dispatch-route-planner.page'),
      },
      {
        path: ':id',
        canActivate: [hasPermissionGuard(PERMISSIONS_VENTAS.VER_RUTAS_DE_DESPACHO)],
        loadComponent: () =>
          import('@features/sales/dispatch-routes/pages/dispatch-route-planner/dispatch-route-planner.page'),
      },
    ],
  },
  {
    path: '',
    component: AppLayoutComponent,
    canActivate: [isAuthenticatedGuard, mustChangePasswordGuard, cashierOnlyRedirectGuard],
    // BUG-IOS-01 (GH #490/#491) — presupuesto de pila en iOS.
    // JavaScriptCore en iPhone ofrece ~9.200 frames de pila frente a ~12.400 de V8; una navegación
    // a `/` con los guards duplicados (`canActivate` + `canActivateChild` en cada módulo hijo)
    // hacía correr cada guard DOS veces por navegación, apilando dos cadenas RxJS completas y
    // reventando con `RangeError: Maximum call stack size exceeded`.
    //
    // Consolidación: la autenticación se re-verifica UNA sola vez aquí, en el `canActivateChild`
    // del layout raíz, y vale para TODOS los módulos hijos. Los hijos conservan únicamente su
    // `canActivate` (chequeo de módulo/permiso al entrar al módulo).
    //
    // REGLA ANTI-REGRESIÓN: ningún hijo de este layout debe volver a declarar `canActivateChild`
    // — reintroducirlo devuelve la doble pasada de guards y con ella el RangeError en iPhone.
    // Verificado por `src/app.routes.spec.ts`.
    canActivateChild: [isAuthenticatedGuard],
    children: [
      {
        // Ruta técnica del pull-to-refresh móvil (`PullToRefreshService`): destino
        // intermedio para forzar la recreación de la página actual. Nunca se navega
        // aquí desde la UI y no lleva `breadcrumb` a propósito.
        path: '_reload',
        loadComponent: () => import('@shared/components/pull-to-refresh/reload-outlet.component'),
      },
      {
        path: 'dashboard',
        data: { breadcrumb: 'Tablero' },
        loadComponent: () => import('@pages/dashboard/dashboard.page'),
      },
      {
        path: 'reseller',
        data: { breadcrumb: 'Portal reseller' },
        canActivate: [isResellerUserGuard, hasPermissionGuard(PERMISSIONS_GESTION_DE_RESELLERS.VER_DASHBOARD_RESELLER)],
        loadComponent: () => import('@features/reseller-portal/pages/reseller-dashboard.page'),
      },
      {
        path: 'empty',
        data: { breadcrumb: 'empty' },
        loadComponent: () => import('@pages/empty/empty'),
      },
      {
        path: 'ayuda',
        data: { breadcrumb: 'Ayuda' },
        loadComponent: () => import('@pages/help/help.page'),
      },
      {
        path: 'notificaciones',
        data: { breadcrumb: 'Notificaciones' },
        loadChildren: () => import('@core/notifications/notifications.routes'),
      },
      {
        path: 'sales',
        data: { breadcrumb: 'Ventas' },
        canActivate: [hasAnyModuleAccessGuard([PERMISSION_MODULES.VENTAS])],
        loadChildren: () => import('@features/sales/sales.routes'),
      },
      {
        path: 'purchases',
        data: { breadcrumb: 'Compras' },
        canActivate: [hasModuleAccessGuard(PERMISSION_MODULES.COMPRAS)],
        loadChildren: () => import('@features/purchases/purchases.routes'),
      },
      {
        path: 'inventory',
        data: { breadcrumb: 'Inventario' },
        canActivate: [hasModuleAccessGuard(PERMISSION_MODULES.INVENTARIO)],
        loadChildren: () => import('@features/inventory/inventory.routes'),
      },
      {
        path: 'crm',
        data: { breadcrumb: 'CRM' },
        canActivate: [hasModuleAccessGuard(PERMISSION_MODULES.CRM)],
        loadChildren: () => import('@features/crm/crm.routes'),
      },
      {
        path: 'finance',
        data: { breadcrumb: 'Finanzas' },
        canActivate: [hasModuleAccessGuard(PERMISSION_MODULES.FINANZAS)],
        loadChildren: () => import('@features/finance/finance.routes'),
      },
      {
        path: 'accounting',
        data: { breadcrumb: 'Contabilidad' },
        canActivate: [hasModuleAccessGuard(PERMISSION_MODULES.CONTABILIDAD)],
        loadChildren: () => import('@features/accounting/accounting.routes'),
      },
      {
        path: 'manufacturing',
        data: { breadcrumb: 'Fabricación' },
        canActivate: [hasModuleAccessGuard(PERMISSION_MODULES.FABRICACION)],
        loadChildren: () => import('@features/manufacturing/manufacturing.routes'),
      },
      {
        path: 'rrhh',
        data: { breadcrumb: 'Recursos Humanos' },
        canActivate: [hasModuleAccessGuard(PERMISSION_MODULES.RECURSOS_HUMANOS)],
        loadChildren: () => import('@features/rrhh/rrhh.routes').then((m) => m.rrhhRoutes),
      },
      {
        path: 'payroll',
        data: { breadcrumb: 'Nómina' },
        canActivate: [hasModuleAccessGuard(PERMISSION_MODULES.NOMINA)],
        loadChildren: () => import('@features/payroll/payroll.routes').then((m) => m.payrollRoutes),
      },
      {
        path: 'ecommerce',
        data: { breadcrumb: 'E-Commerce' },
        canActivate: [hasModuleAccessGuard(PERMISSION_MODULES.E_COMMERCE)],
        loadChildren: () => import('@features/ecommerce/ecommerce.routes'),
      },
      {
        path: 'general-settings',
        data: { breadcrumb: 'Configuración General' },
        canActivate: [hasModuleAccessGuard(PERMISSION_MODULES.CONFIGURACIONES)],
        loadChildren: () => import('@general-settings/general-settings.routes'),
      },
      {
        path: 'tributario',
        data: { breadcrumb: 'Tributario' },
        canActivate: [hasModuleAccessGuard(PERMISSION_MODULES.TRIBUTARIO)],
        loadChildren: () => import('@features/tax-management/tax-management.routes'),
      },
      {
        path: 'proyectos',
        data: { breadcrumb: 'Proyectos y Tareas' },
        canActivate: [hasModuleAccessGuard(PERMISSION_MODULES.PROYECTOS_Y_TAREAS)],
        loadChildren: () => import('@features/projects/projects.routes'),
      },
      {
        path: 'support',
        data: { breadcrumb: 'Soporte' },
        loadChildren: () => import('@support/support.routes'),
      },
      {
        path: 'soporte-admin',
        data: { breadcrumb: 'Gestión de Soporte' },
        canActivate: [isDefaultTenantGuard, hasModuleAccessGuard(PERMISSION_MODULES.SOPORTE)],
        loadChildren: () => import('@admin/support-tickets/support-tickets-admin.routes'),
      },
      {
        path: 'admin',
        data: { breadcrumb: 'Gestión Empresarial' },
        canActivate: [isDefaultTenantGuard],
        loadChildren: () => import('@admin/admin.routes'),
      },
      {
        path: 'soporte',
        data: { breadcrumb: 'Soporte' },
        canActivate: [isAuthenticatedGuard, hasPermissionGuard(PERMISSIONS_SOPORTE.VER_TICKETS)],
        loadChildren: () => import('./app/features/support/support.routes'),
      },
      {
        path: 'pos/settings',
        data: { breadcrumb: 'Punto de Venta - Configuración' },
        canActivate: [hasModuleAccessGuard(PERMISSION_MODULES.PUNTO_DE_VENTA)],
        loadChildren: () =>
          import('./app/features/pos/settings/pos-settings.routes').then((m) => m.POS_SETTINGS_ROUTES),
      },
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
    ],
  },
  {
    path: 'pos',
    // `mustChangePasswordGuard` va también aquí: POS cuelga de la raíz, NO del AppLayout, así que
    // no hereda el guard del shell. Sin esta línea, quien todavía usa la contraseña provisional
    // que le llegó por correo entraba a operar caja escribiendo `/pos` en la barra de direcciones,
    // y esa contraseña —que pasó por manos ajenas— seguía siendo válida indefinidamente.
    canActivate: [isAuthenticatedGuard, mustChangePasswordGuard, posAccessGuard],
    loadChildren: () => import('./app/features/pos/pos.routes').then((m) => m.POS_ROUTES),
  },
  // La pantalla de cambio obligatorio de contraseña se declara AQUÍ, fuera del bloque `auth`, y
  // ANTES que él para que gane por orden de declaración.
  //
  // No es una preferencia de organización: `path: 'auth'` lleva `notAuthenticatedGuard`, y a esta
  // pantalla se llega precisamente YA AUTENTICADO. Declarada dentro, el guard del padre corría
  // primero, veía la sesión activa y devolvía al usuario a `/`, que `mustChangePasswordGuard`
  // volvía a desviar aquí: un bucle infinito que dejaba fuera de la aplicación a TODO usuario con
  // contraseña provisional —es decir, a cada propietario de distribuidor recién creado y a cada
  // restablecimiento hecho desde administración—. La URL se mantiene (`/auth/change-password`)
  // porque es la que declara `CHANGE_PASSWORD_ROUTE` y la que compara el guard.
  {
    path: 'auth/change-password',
    canActivate: [isAuthenticatedGuard],
    loadComponent: () => import('@auth/pages/change-password.page'),
  },
  {
    path: 'auth',
    canActivate: [notAuthenticatedGuard],
    loadChildren: () => import('@auth/auth.routes'),
  },
  {
    path: 'notfound',
    loadComponent: () => import('@pages/notfound/not-found.page').then((c) => c.NotFoundPage),
  },
  {
    path: 'access-denied',
    loadComponent: () => import('@pages/access-denied/access-denied.page').then((c) => c.AccessDeniedPage),
  },
  // Comodín 404: el `staleVersionGuard` intenta recuperar la versión nueva si la ruta no existe
  // por estar el cliente pegado a un bundle viejo (ver guard). Si no hay update, muestra el 404.
  // Carga el componente directamente (no `redirectTo`) porque Angular no permite `redirectTo` +
  // `canMatch` juntos (NG04014), y así además se preserva la URL intentada para la recuperación.
  {
    path: '**',
    canMatch: [staleVersionGuard],
    loadComponent: () => import('@pages/notfound/not-found.page').then((c) => c.NotFoundPage),
  },
];
